import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Layout from "../layouts/Layout";
import {
  API_BASE_URL,
  FONT_HEADING,
  FONT_UI,
  SKIN_THEMES,
} from "../layouts/constants";

/* ─────────────────────────── Skin ─────────────────────────── */

function useSkin() {
  const skinId = localStorage.getItem("skinTheme") || "navy";
  return SKIN_THEMES.find((s) => s.id === skinId) || SKIN_THEMES[0];
}

/* ─────────────────────────── Formatters ─────────────────────────── */

function formatValue(value, fallback = "") {
  return value === null || value === undefined || value === ""
    ? fallback
    : String(value);
}

/**
 * Display a SQL datetime string as IST wall-clock time.
 * Never passes through new Date() to avoid timezone shift.
 * Input:  "2026-05-29 16:55:00" or "2026-05-29T16:55:00" or "2026-05-29T16:55:00.000Z"
 * Output: "29 May 2026, 04:55:00 pm"
 */
function formatDateTime(value, fallback = "") {
  if (!value) return fallback;

  const text = String(value)
    .replace("T", " ")
    .replace("Z", "")
    .split(".")[0]
    .trim();

  const [datePart, timePart] = text.split(" ");
  if (!datePart || !timePart) return text;

  const [year, month, day] = datePart.split("-").map(Number);
  const [hStr, mStr, sStr = "00"] = timePart.split(":");
  const hour = Number(hStr);
  const ampm = hour >= 12 ? "pm" : "am";
  const hour12 = hour % 12 || 12;

  const monthNames = [
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
  ];

  return `${String(day).padStart(2, "0")} ${monthNames[month - 1]} ${year}, ${String(
    hour12
  ).padStart(2, "0")}:${mStr}:${sStr} ${ampm}`;
}

/**
 * Convert a stored SQL datetime string to a datetime-local input value.
 * Strips Z and milliseconds so the browser input shows the correct IST time.
 * Input:  "2026-05-29T16:55:00.000Z" or "2026-05-29 16:55:00"
 * Output: "2026-05-29T16:55"
 */
function toDateTimeLocal(value) {
  if (!value) return "";

  return String(value)
    .replace("T", " ")
    .replace("Z", "")
    .split(".")[0]
    .trim()
    .replace(" ", "T")
    .slice(0, 16);
}

/**
 * Parse a datetime-local string for comparison ONLY (no storage).
 * Uses Date.UTC to avoid any timezone offset.
 * Input:  "2026-05-29T16:55" or "2026-05-29T16:55:00"
 * Output: ms number or null
 */
function parseLocalForCompare(str) {
  if (!str) return null;

  const s = String(str).trim().replace("T", " ");
  const [datePart, timePart] = s.split(" ");

  if (!datePart || !timePart) return null;

  const [year, month, day]      = datePart.split("-").map(Number);
  const [hour, minute, sec = 0] = timePart.split(":").map(Number);

  if (
    !Number.isFinite(year)   ||
    !Number.isFinite(month)  ||
    !Number.isFinite(day)    ||
    !Number.isFinite(hour)   ||
    !Number.isFinite(minute)
  ) return null;

  return Date.UTC(year, month - 1, day, hour, minute, sec);
}

/* ─────────────────────────── Constants ─────────────────────────── */

const RECORDS_PER_PAGE = 10;

/* ─────────────────────────── Main component ─────────────────────────── */

export default function RouteAllocations() {
  const navigate = useNavigate();
  const location = useLocation();

  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const routeId          = Number(params.get("route_id") || 0);
  const initialRouteName = params.get("route_name") || "";
  const initialSearch    = params.get("q") || "";
  const initialPage      = Number(params.get("page") || 1);

  /* ── State ── */
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [deletingId,   setDeletingId]   = useState(null);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage,   setErrorMessage]   = useState("");

  const [dashboardData, setDashboardData] = useState({
    company_name: "", licensedFor: "", admin_active: 0,
    company_address: "", company_phone: "", admin_email: "",
    company_logo: "", user_map: "",
  });

  // Keep a ref so callbacks always see the latest dashboardData
  const dashboardDataRef = useRef(dashboardData);
  useEffect(() => { dashboardDataRef.current = dashboardData; }, [dashboardData]);

  const [routeName,     setRouteName]     = useState(initialRouteName);
  const [searchInput,   setSearchInput]   = useState(initialSearch);
  const [search,        setSearch]        = useState(initialSearch);
  const [page,          setPage]          = useState(initialPage > 0 ? initialPage : 1);

  const [allocations,   setAllocations]   = useState([]);
  const [guards,        setGuards]        = useState([]);
  const [totalRecords,  setTotalRecords]  = useState(0);
  const [totalPages,    setTotalPages]    = useState(1);
  const [allRows,       setAllRows]       = useState([]); // full unfiltered list for client search

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState({
    allocation_id:   "",
    guard_member_id: "",
    shift_label:     "",
    start_time:      "",
    end_time:        "",
  });

  /* ── Auto-clear messages ── */
  useEffect(() => {
    if (!successMessage && !errorMessage) return;
    const timer = setTimeout(() => {
      setSuccessMessage("");
      setErrorMessage("");
    }, 5000);
    return () => clearTimeout(timer);
  }, [successMessage, errorMessage]);

  /* ── Helpers ── */
  function getToken() {
    return localStorage.getItem("token") || localStorage.getItem("authToken") || "";
  }

  function getLocalDashboard() {
    const userData = JSON.parse(localStorage.getItem("userData") || "{}");
    return {
      company_name:    localStorage.getItem("company_name")    || userData.company_name    || "",
      licensedFor:     localStorage.getItem("licensedFor")     || userData.licensedFor     || "",
      admin_active:    Number(localStorage.getItem("admin_active") ?? userData.admin_active ?? 0),
      company_address: localStorage.getItem("company_address") || userData.company_address || "",
      company_phone:   localStorage.getItem("company_phone")   || userData.company_phone   || "",
      admin_email:     localStorage.getItem("admin_email")     || userData.admin_email     || "",
      company_logo:    localStorage.getItem("company_logo")    || userData.company_logo    || "",
      user_map:        localStorage.getItem("user_map")        || userData.user_map        || "",
    };
  }

  /* ── Fetch guards ── */
  const fetchGuards = useCallback(async () => {
    try {
      const res = await fetch(`${API_BASE_URL}/users/`, {
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
        cache: "no-store",
      });

      const data = await res.json();
      setGuards(Array.isArray(data.data) ? data.data : []);
    } catch (err) {
      console.error("Fetch guards error:", err);
      setGuards([]);
    }
  }, []);

  /* ── Fetch allocations (full list from API, then client-side paginate) ── */
  const fetchAllocations = useCallback(async () => {
    try {
      const res = await fetch(
        `${API_BASE_URL}/route-allocation/${encodeURIComponent(routeId)}`,
        {
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${getToken()}` },
          cache: "no-store",
        }
      );

      if (!res.ok) throw new Error("Failed to fetch allocations");

      const data = await res.json();
      return Array.isArray(data?.data) ? data.data : [];
    } catch (err) {
      console.error("Fetch allocations error:", err);
      return [];
    }
  }, [routeId]);

  /* ── Apply search + pagination on the client ── */
  const applySearchAndPage = useCallback(
    (rows, currentSearch, currentPage) => {
      let filtered = rows;

      if (currentSearch?.trim()) {
        const kw = currentSearch.trim().toLowerCase();

        filtered = rows.filter((row) => {
          const guardText = String(
            row.guard_label || row.guard_name || row.username || row.guard_member_id || ""
          ).toLowerCase();
          const shiftText = String(row.shift_label || "").toLowerCase();

          return guardText.includes(kw) || shiftText.includes(kw);
        });
      }

      const total    = filtered.length;
      const pages    = Math.max(1, Math.ceil(total / RECORDS_PER_PAGE));
      const safePage = Math.min(currentPage, pages);
      const start    = (safePage - 1) * RECORDS_PER_PAGE;
      const paged    = filtered.slice(start, start + RECORDS_PER_PAGE);

      setAllocations(paged);
      setTotalRecords(total);
      setTotalPages(pages);
    },
    []
  );

  /* ── Full page load ── */
  const loadPage = useCallback(
    async (currentSearch, currentPage) => {
      setLoading(true);
      setErrorMessage("");

      try {
        const token = getToken();

        const [dashboardRes, rows] = await Promise.all([
          fetch(`${API_BASE_URL}/dashboard/admin/dashboard`, {
            headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          }),
          fetchAllocations(),
        ]);

        // Dashboard data (non-blocking if it fails)
        if (dashboardRes.ok) {
          const dashboardResult = await dashboardRes.json();
          const cd = dashboardResult?.data?.companyDetails || {};
          const local = dashboardDataRef.current;

          const merged = {
            company_name:    cd.company_name    ?? local.company_name    ?? "",
            licensedFor:     cd.licensedFor     ?? local.licensedFor     ?? "",
            admin_active:    Number(cd.admin_active ?? local.admin_active ?? 0),
            company_address: cd.company_address ?? local.company_address ?? "",
            company_phone:   cd.company_phone   ?? local.company_phone   ?? "",
            admin_email:     cd.admin_email     ?? local.admin_email     ?? "",
            company_logo:    cd.company_logo    ?? local.company_logo    ?? "",
            user_map:        cd.user_map        ?? local.user_map        ?? "",
          };

          setDashboardData(merged);
        }

        setAllRows(rows);
        setRouteName(initialRouteName || `Route #${routeId}`);
        applySearchAndPage(rows, currentSearch, currentPage);
      } catch (err) {
        console.error("loadPage error:", err);
        setErrorMessage(err.message || "Failed to load route allocations.");
        setAllocations([]);
        setTotalRecords(0);
        setTotalPages(1);
      } finally {
        setLoading(false);
      }
    },
    [fetchAllocations, applySearchAndPage, initialRouteName, routeId]
  );

  /* ── Initial load ── */
  useEffect(() => {
    const token     = getToken();
    const isLoggedIn = localStorage.getItem("isLoggedIn");

    if (!token || !isLoggedIn) { navigate("/"); return; }

    if (!routeId) {
      setErrorMessage("Missing or invalid route ID.");
      setLoading(false);
      return;
    }

    setDashboardData(getLocalDashboard());
    loadPage(search, page);
    fetchGuards();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /* ── Re-apply search/page when they change (no full API refetch needed) ── */
  useEffect(() => {
    applySearchAndPage(allRows, search, page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, page]);

  /* ── URL sync ── */
  const updateUrl = useCallback(
    (nextPage, nextSearch) => {
      const query = new URLSearchParams();
      query.set("route_id", String(routeId));
      if (routeName)              query.set("route_name", routeName);
      if (nextSearch?.trim())     query.set("q",          nextSearch.trim());
      if (nextPage > 1)           query.set("page",       String(nextPage));
      navigate(`/route_allocation?${query.toString()}`);
    },
    [navigate, routeId, routeName]
  );

  /* ── Search handlers ── */
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    const nextSearch = searchInput.trim();
    setPage(1);
    setSearch(nextSearch);
    updateUrl(1, nextSearch);
  };

  const clearSearch = () => {
    setSearchInput("");
    setSearch("");
    setPage(1);
    updateUrl(1, "");
  };

  const handlePageChange = (nextPage) => {
    setPage(nextPage);
    updateUrl(nextPage, search);
  };

  /* ── Map button ── */
  const openRouteMap = (allocation) => {
    const url = `/admin_route_tracking?route_id=${encodeURIComponent(
      routeId
    )}&user_id=${encodeURIComponent(
      allocation.guard_member_id
    )}&alloc_id=${encodeURIComponent(allocation.allocation_id)}`;
    window.open(url, "_blank");
  };

  /* ── Edit modal ── */
  const openEditModal = (allocation) => {
    setEditForm({
      allocation_id:   allocation.allocation_id   || "",
      guard_member_id: allocation.guard_member_id || "",
      shift_label:     allocation.shift_label     || "",
      start_time:      toDateTimeLocal(allocation.start_time),
      end_time:        toDateTimeLocal(allocation.end_time),
    });
    setEditModalOpen(true);
  };

  const closeEditModal = () => {
    setEditModalOpen(false);
    setEditForm({
      allocation_id: "", guard_member_id: "",
      shift_label: "", start_time: "", end_time: "",
    });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const saveEditAllocation = async () => {
    if (
      !editForm.allocation_id ||
      !editForm.guard_member_id ||
      !editForm.shift_label.trim() ||
      !editForm.start_time ||
      !editForm.end_time
    ) {
      window.alert("Please complete all fields.");
      return;
    }

    // ── Validate times without new Date() ──
    const startMs = parseLocalForCompare(editForm.start_time);
    const endMs   = parseLocalForCompare(editForm.end_time);

    if (!startMs || !endMs) {
      window.alert("Invalid date/time format.");
      return;
    }

    if (endMs <= startMs) {
      window.alert("End time must be after start time.");
      return;
    }

    try {
      setSaving(true);

      // Send raw datetime-local strings — backend must use toSqlDateTime() / CONVERT
      const res = await fetch(
        `${API_BASE_URL}/route-allocation/${editForm.allocation_id}`,
        {
          method:  "PUT",
          headers: {
            "Content-Type": "application/json",
            Authorization:  `Bearer ${getToken()}`,
          },
          body: JSON.stringify({
            guard_member_id: editForm.guard_member_id,
            shift_label:     editForm.shift_label.trim(),
            start_time:      editForm.start_time,  // raw "2026-05-29T16:55" — no new Date()
            end_time:        editForm.end_time,    // raw "2026-05-29T18:55" — no new Date()
          }),
        }
      );

      const data = await res.json();

      if (!res.ok || data.success === false) {
        throw new Error(data.message || "Failed to update allocation");
      }

      setSuccessMessage(data.message || "Allocation updated successfully.");
      closeEditModal();

      // Refresh the full list so table shows new values
      const rows = await fetchAllocations();
      setAllRows(rows);
      applySearchAndPage(rows, search, page);
    } catch (err) {
      console.error("Update allocation error:", err);
      window.alert(err.message || "Unexpected error updating allocation.");
    } finally {
      setSaving(false);
    }
  };

  /* ── Delete ── */
  const deleteAllocation = async (allocationId) => {
    if (!window.confirm("Delete this allocation? This cannot be undone.")) return;

    try {
      setDeletingId(allocationId);

      const res = await fetch(`${API_BASE_URL}/route-allocation/${allocationId}`, {
        method:  "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization:  `Bearer ${getToken()}`,
        },
      });

      const data = await res.json();
      if (!res.ok || data.success === false) throw new Error(data.message || "Failed to delete");

      setSuccessMessage(data.message || "Allocation deleted successfully.");

      // Refresh list after delete
      const rows = await fetchAllocations();
      setAllRows(rows);
      applySearchAndPage(rows, search, page);
    } catch (err) {
      console.error("Delete allocation error:", err);
      window.alert(err.message || "Unexpected error deleting allocation.");
    } finally {
      setDeletingId(null);
    }
  };

  /* ── Pagination items ── */
  const paginationItems = useMemo(() => {
    const items = [];
    const start = Math.max(1, page - 2);
    const end   = Math.min(totalPages, page + 2);

    if (start > 1) { items.push(1); if (start > 2) items.push("..."); }
    for (let i = start; i <= end; i++) items.push(i);
    if (end < totalPages) { if (end < totalPages - 1) items.push("..."); items.push(totalPages); }

    return items;
  }, [page, totalPages]);

  const routePage = useMemo(
    () => (dashboardData.user_map === "google" ? "/create_route" : "/create-route"),
    [dashboardData.user_map]
  );

  /* ─────────────────────────── Render ─────────────────────────── */

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-100 border-t-blue-600 animate-spin mb-3" />
        <p className="text-slate-500" style={{ fontFamily: FONT_UI }}>
          Loading route allocations...
        </p>
      </div>
    );
  }

  return (
    <Layout
      dashboardData={dashboardData}
      routePage={routePage}
      activePage="routes"
      activeMenu="routes"
    >
      <PageHeader
        routeName={routeName}
        routeId={routeId}
        onBack={() => navigate("/route_list")}
      />

      {successMessage ? <Alert type="success" message={successMessage} /> : null}
      {errorMessage   ? <Alert type="error"   message={errorMessage}   /> : null}

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6">
        {/* ── Header ── */}
        <div className="mb-5 flex justify-between items-center gap-3 flex-wrap">
          <div>
            <h3
              className="m-0 text-[22px] font-normal text-slate-900"
              style={{ fontFamily: FONT_HEADING }}
            >
              Allocations
            </h3>

            <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
              View guard allocations for this selected route
            </p>
          </div>

          <Badge>{totalRecords} total</Badge>
        </div>

        {/* ── Stats ── */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-5">
          <StatusCard label="Total Allocations" value={totalRecords} />
          <StatusCard label="Page Status"        value={`${page} / ${totalPages}`} />
        </div>

        {/* ── Search ── */}
        <form onSubmit={handleSearchSubmit} className="flex gap-3 flex-wrap items-center">
          <Input
            type="text"
            placeholder="Search guard / shift..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="flex-1 min-w-[260px]"
          />

          <button
            type="submit"
            className="px-5 py-3 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20"
            style={{ fontFamily: FONT_UI }}
          >
            Search
          </button>

          {search ? (
            <button
              type="button"
              onClick={clearSearch}
              className="px-5 py-3 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
              style={{ fontFamily: FONT_UI }}
            >
              Clear
            </button>
          ) : null}
        </form>

        {/* ── Table ── */}
        <div className="mt-5 w-full overflow-x-auto border border-slate-200 rounded-2xl">
          <table className="w-full border-collapse bg-white min-w-[900px]">
            <thead>
              <tr className="bg-slate-50">
                <Th>#</Th>
                <Th>Guard</Th>
                <Th>Shift</Th>
                <Th>Start</Th>
                <Th>End</Th>
                <Th>Actions</Th>
              </tr>
            </thead>

            <tbody>
              {allocations.length > 0 ? (
                allocations.map((row, index) => (
                  <tr key={row.allocation_id} className="hover:bg-slate-50">
                    <Td>{(page - 1) * RECORDS_PER_PAGE + index + 1}</Td>

                    <Td>
                      <strong>
                        {formatValue(
                          row.guard_label || row.guard_name || row.username,
                          row.guard_member_id
                        )}
                      </strong>{" "}
                      <small className="text-slate-500">({row.guard_member_id})</small>
                    </Td>

                    <Td>
                      <Badge>{formatValue(row.shift_label)}</Badge>
                    </Td>

                    <Td>{formatDateTime(row.start_time, "Not specified")}</Td>
                    <Td>{formatDateTime(row.end_time,   "Not specified")}</Td>

                    <Td>
                      <div className="flex gap-2 flex-wrap">
                        <button
                          type="button"
                          onClick={() => openRouteMap(row)}
                          className="px-4 py-2 rounded-xl bg-green-500 text-white text-sm font-semibold hover:bg-green-600"
                          style={{ fontFamily: FONT_UI }}
                        >
                          Map
                        </button>

                        <button
                          type="button"
                          onClick={() => openEditModal(row)}
                          className="px-4 py-2 rounded-xl bg-amber-500 text-white text-sm font-semibold hover:bg-amber-600"
                          style={{ fontFamily: FONT_UI }}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          onClick={() => deleteAllocation(row.allocation_id)}
                          className="px-4 py-2 rounded-xl bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-60 disabled:cursor-not-allowed"
                          style={{ fontFamily: FONT_UI }}
                          disabled={deletingId === row.allocation_id}
                        >
                          {deletingId === row.allocation_id ? "Deleting..." : "Delete"}
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    className="px-4 py-8 text-center text-sm text-slate-500"
                    colSpan={6}
                    style={{ fontFamily: FONT_UI }}
                  >
                    {search
                      ? `No allocations match "${search}".`
                      : "No allocations found for this route."}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* ── Pagination ── */}
        {totalPages > 1 && (
          <div className="flex items-center justify-center gap-3 flex-wrap mt-5">
            {page > 1 && (
              <PageButton onClick={() => handlePageChange(page - 1)}>
                Previous
              </PageButton>
            )}

            {paginationItems.map((item, index) =>
              item === "..." ? (
                <span
                  key={`dots-${index}`}
                  className="text-slate-500 text-lg"
                  style={{ fontFamily: FONT_UI }}
                >
                  ...
                </span>
              ) : (
                <PageButton
                  key={item}
                  active={item === page}
                  onClick={() => handlePageChange(item)}
                >
                  {item}
                </PageButton>
              )
            )}

            {page < totalPages && (
              <PageButton onClick={() => handlePageChange(page + 1)}>
                Next
              </PageButton>
            )}
          </div>
        )}
      </div>

      {/* ── Edit Modal ── */}
      {editModalOpen && (
        <Modal onClose={closeEditModal}>
          <ModalHeader title="Edit Allocation" onClose={closeEditModal} />

          <div className="p-6 bg-slate-50 overflow-y-auto max-h-[60vh]">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="md:col-span-2">
                <Field label="Guard">
                  <Select
                    name="guard_member_id"
                    value={editForm.guard_member_id}
                    onChange={handleEditChange}
                    required
                  >
                    <option value="">
                      {guards.length ? "-- Select Guard --" : "Loading guards..."}
                    </option>

                    {guards.map((guard) => (
                      <option key={guard.member_id} value={guard.member_id}>
                        {guard.username} ({guard.member_id})
                      </option>
                    ))}
                  </Select>
                </Field>
              </div>

              <div className="md:col-span-2">
                <Field label="Shift Label">
                  <Input
                    type="text"
                    name="shift_label"
                    value={editForm.shift_label}
                    onChange={handleEditChange}
                    placeholder="Morning / Evening / Night / Custom"
                    required
                  />
                </Field>
              </div>

              <Field label="Start Date & Time">
                <Input
                  type="datetime-local"
                  name="start_time"
                  value={editForm.start_time}
                  onChange={handleEditChange}
                  required
                />
              </Field>

              <Field label="End Date & Time">
                <Input
                  type="datetime-local"
                  name="end_time"
                  value={editForm.end_time}
                  onChange={handleEditChange}
                  required
                />
              </Field>
            </div>
          </div>

          <div className="px-6 py-4 bg-white border-t border-slate-200 flex justify-end gap-3 flex-wrap">
            <button
              type="button"
              onClick={closeEditModal}
              className="px-5 py-2.5 rounded-xl border border-slate-200 bg-white text-slate-700 text-sm font-semibold hover:bg-slate-50"
              style={{ fontFamily: FONT_UI }}
            >
              Close
            </button>

            <button
              type="button"
              onClick={saveEditAllocation}
              className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20 disabled:opacity-60 disabled:cursor-not-allowed"
              style={{ fontFamily: FONT_UI }}
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </Modal>
      )}
    </Layout>
  );
}

/* ─────────────────────────── Sub-components ─────────────────────────── */

function PageHeader({ routeName, routeId, onBack }) {
  const skin = useSkin();

  return (
    <>
      <div className="mb-6">
        <h2
          className="m-0 text-[26px] font-normal text-slate-900 tracking-tight"
          style={{ fontFamily: FONT_HEADING }}
        >
          Route Allocations
        </h2>

        <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
          Route: <strong>{formatValue(routeName, `Route #${routeId}`)}</strong> • ID: {routeId}
        </p>
      </div>

      <div className="bg-white rounded-2xl px-7 py-6 shadow-sm border border-slate-100 mb-6 flex justify-between items-center gap-4 flex-wrap">
        <div>
          <h3
            className="m-0 text-[22px] font-normal text-slate-900"
            style={{ fontFamily: FONT_HEADING }}
          >
            Allocation List
          </h3>

          <p className="mt-1 text-sm text-slate-500" style={{ fontFamily: FONT_UI }}>
            Manage route guard allocations, timings, tracking map, edit and delete actions
          </p>
        </div>

        <button
          type="button"
          onClick={onBack}
          className="px-5 py-2.5 rounded-xl text-white text-sm font-semibold shadow-lg shadow-blue-500/20"
          style={{ background: skin.gradient, fontFamily: FONT_UI }}
        >
          Back to Routes
        </button>
      </div>
    </>
  );
}

function Alert({ type, message }) {
  const isSuccess = type === "success";
  return (
    <div
      className={`mb-5 px-4 py-3.5 rounded-xl text-sm font-medium border ${
        isSuccess
          ? "bg-green-50 border-green-200 text-green-700"
          : "bg-red-50 border-red-200 text-red-700"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {message}
    </div>
  );
}

function StatusCard({ label, value }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4">
      <div className="text-sm text-slate-500 mb-1.5" style={{ fontFamily: FONT_UI }}>
        {label}
      </div>
      <div className="text-[22px] font-bold text-slate-900" style={{ fontFamily: FONT_HEADING }}>
        {value}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label
        className="block mb-2 text-sm font-semibold text-slate-900"
        style={{ fontFamily: FONT_UI }}
      >
        {label}
      </label>
      {children}
    </div>
  );
}

function Input({ className = "", ...props }) {
  return (
    <input
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 disabled:bg-slate-100 disabled:text-slate-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    />
  );
}

function Select({ className = "", children, ...props }) {
  return (
    <select
      {...props}
      className={`w-full px-3.5 py-3 rounded-xl border border-slate-200 outline-none text-sm text-slate-900 bg-white box-border focus:ring-2 focus:ring-blue-100 focus:border-blue-500 ${className}`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </select>
  );
}

function Badge({ children }) {
  return (
    <span
      className="inline-flex items-center px-3 py-1.5 rounded-full bg-indigo-50 text-indigo-700 font-bold text-xs"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </span>
  );
}

function Th({ children }) {
  return (
    <th
      className="text-left px-4 py-3.5 text-sm text-slate-900 bg-slate-50 border-b border-slate-200 whitespace-nowrap"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </th>
  );
}

function Td({ children }) {
  return (
    <td
      className="px-4 py-3.5 text-sm text-slate-700 border-b border-slate-100 align-top"
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </td>
  );
}

function PageButton({ children, active = false, ...props }) {
  return (
    <button
      type="button"
      {...props}
      className={`min-w-[46px] px-4 py-2.5 rounded-xl text-sm font-semibold ${
        active
          ? "text-white bg-gradient-to-br from-slate-800 to-blue-600 shadow-lg shadow-blue-500/20"
          : "border border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
      }`}
      style={{ fontFamily: FONT_UI }}
    >
      {children}
    </button>
  );
}

function Modal({ children, onClose }) {
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] p-5"
      onClick={onClose}
    >
      <div
        className="w-full max-w-[720px] bg-white rounded-2xl overflow-hidden shadow-2xl max-h-[92vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHeader({ title, onClose }) {
  return (
    <div className="px-6 py-4 bg-blue-700 text-white flex items-center justify-between flex-shrink-0">
      <h2 className="m-0 text-[22px]" style={{ fontFamily: FONT_HEADING }}>
        {title}
      </h2>
      <button
        type="button"
        onClick={onClose}
        className="border-0 bg-transparent text-white text-3xl leading-none cursor-pointer"
      >
        ×
      </button>
    </div>
  );
}